import React from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { useSelector, useDispatch } from "react-redux";

import { getUuidUser } from "@helpers/getUuidUser";
import { EtkTable } from "@components/Common/EtkTable";

import { store } from "../../../../components/VerticalLayout/store";
import { useAuthHeader } from "../../../../api/headersKeycloak";
import { RootState, AppDispatch } from "../../../../redux/store";
import { setGlobalDataIsUpdated } from "../../../../redux/slices/fidelitySlice";

import FidelityForm from "../FidelityFranchise/FidelityFormWrapper";

import { useEtkColumns } from "./useEtkColumns";
import { useShopsFidelitySync } from "./useShopsFidelitySync";
import TableToolbar from "./TableToolbar";
import ShopsFidelityModals from "./ShopsFidelityModals";

import "./index.css";
import "@assets/swal.css";
import "@assets/theme.css";
import "react-toastify/dist/ReactToastify.css";

type Props = {
    isFidelityArchived: boolean;
    setIsFidelityArchived: React.Dispatch<React.SetStateAction<boolean>>;
    setLimit: (v: number) => void;
};

function buildTableUrls(userID: number, franchiseID: string, uuidUser: string) {
    const base = process.env.REACT_APP_API_V2_URL;
    return {
        urlArchive: `${base}/settings/general/fidelity?userId=${userID}&franchiseId=${franchiseID}&uuidUser=${uuidUser}`,
        urlActive: `${base}/settings/general/fidelity?userId=${userID}&franchiseId=${franchiseID}&type=activated&uuidUser=${uuidUser}`,
    };
}

export default function ShopsFidelity({
    isFidelityArchived,
    setIsFidelityArchived,
    setLimit,
}: Props) {
    const { oneShop, userID, franchiseID } = useSnapshot(store);
    const isAssociateModalOpened = useSelector(
        (s: RootState) => s.fidelity.isAssociateModalOpened
    );
    const globalDataIsUpdated = useSelector(
        (s: RootState) => s.fidelity.globalDataIsUpdated
    );
    const isConsultModalOpened = useSelector(
        (s: RootState) => s.fidelity.isConsultModalOpened
    );
    const modify = useSelector((s: RootState) => s.fidelity.modify);
    const key = useSelector((s: RootState) => s.fidelity.key);
    const dispatch = useDispatch<AppDispatch>();
    const handleSetGlobalDataIsUpdated = React.useCallback(
        (v: React.SetStateAction<number | boolean>) => {
            const next = typeof v === "function" ? v(globalDataIsUpdated) : v;
            dispatch(setGlobalDataIsUpdated(next));
        },
        [dispatch, globalDataIsUpdated]
    );
    const getAuthHeader = useAuthHeader();
    const { t } = useTranslation();

    const [, setLocalFilterActive] = React.useState<boolean>(false);
    const [isShopSelected, setIsShopSelected] = React.useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [isConsult, setIsConsult] = React.useState<boolean>(false);
    const [isHistoryModalOpened, setIsHistoryModalOpened] = React.useState<
        boolean
    >(false);
    const [selectedRows, setSelectedRows] = React.useState<any[]>([]);
    const [isModalOpenedSupport, setIsModalOpenedSupport] = React.useState<
        boolean
    >(false);
    const [codeSupport, setCodeSupport] = React.useState<string>("");
    const [isValidCode, setIsValidCode] = React.useState(false);
    const [dataTable, setDataTable] = React.useState<any>({});
    const [isAddFidelityClicked, setIsAddFidelityClicked] = React.useState<
        boolean
    >(false);
    const [fidelityChoiceIndex, setFidelityChoiceIndex] = React.useState<
        number
    >(0);
    const [fidelity, setFidelity] = React.useState<any>([]);
    const [isAddNewFidelityButtonClicked] = React.useState(false);

    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const uuidUser = getUuidUser();
    const { urlArchive, urlActive } = buildTableUrls(
        userID,
        franchiseID,
        uuidUser
    );

    const requestHeaders = React.useMemo(
        () =>
            ({ "Accept-Language": i18nextLng, ...getAuthHeader } as Record<
                string,
                string
            >),
        [i18nextLng, getAuthHeader]
    );

    const customColumns = useEtkColumns({
        t,
        isFidelityArchived,
        selectedRows,
        setSelectedRows,
        setIsConsult,
        setIsAddFidelityClicked,
        setFidelity,
        setDataIsUpdated: handleSetGlobalDataIsUpdated,
    });

    useShopsFidelitySync({
        t,
        selectedRows,
        dataTable,
        setIsShopSelected,
        isModalOpenedSupport,
        isValidCode,
        codeSupport,
    });

    return (
        <React.Fragment>
            <div
                style={{
                    display: "grid",
                    minHeight: oneShop ? "650px" : "600px",
                    gridTemplateColumns: isAddFidelityClicked
                        ? "2fr 1fr "
                        : "auto",
                    gridColumnGap: "4px",
                    border: 0,
                }}
            >
                <EtkTable
                    refreshKey={`${globalDataIsUpdated}-${key}`}
                    url={isFidelityArchived ? urlArchive : urlActive}
                    headers={requestHeaders}
                    customColumns={customColumns}
                    canExpand
                    canSelect
                    customSelect
                    onDataChange={setDataTable}
                    onSelectionChange={setSelectedRows}
                    toolbarRight={
                        <TableToolbar
                            modify={modify}
                            isFidelityArchived={isFidelityArchived}
                            isShopSelected={isShopSelected}
                            selectedRows={selectedRows}
                            setIsFidelityArchived={setIsFidelityArchived}
                            setLocalFilterActive={setLocalFilterActive}
                            setLimit={setLimit}
                            setGlobalDataIsUpdated={
                                handleSetGlobalDataIsUpdated
                            }
                            t={t}
                        />
                    }
                    showSearch
                    showFilters
                    canResize
                    canMovedCheckboxLeftOnExpand
                    setLocalFilterActive={setLocalFilterActive}
                    dataIsUpdated={globalDataIsUpdated}
                    setDataIsUpdated={handleSetGlobalDataIsUpdated}
                    minHeight={oneShop ? "500px" : "450px"}
                    maxHeight={oneShop ? "500px" : "450px"}
                    name="Fidelity"
                />
                {isAddFidelityClicked ? (
                    <FidelityForm
                        isAddFidelityClicked={isAddFidelityClicked}
                        setIsAddFidelityClicked={setIsAddFidelityClicked}
                        setDataIsUpdated={handleSetGlobalDataIsUpdated}
                        dataFidelity={fidelity}
                        isAddNewFidelityButtonClicked={
                            isAddNewFidelityButtonClicked
                        }
                        setDataFidelity={setFidelity}
                        setLocalFilterActive={setLocalFilterActive}
                        fidelityChoiceIndex={fidelityChoiceIndex}
                        setFidelityChoiceIndex={setFidelityChoiceIndex}
                        setIsConsult={setIsConsult}
                        isConsult={isConsult}
                    />
                ) : null}
            </div>

            <ShopsFidelityModals
                isModalOpenedSupport={isModalOpenedSupport}
                setIsModalOpenedSupport={setIsModalOpenedSupport}
                codeSupport={codeSupport}
                setCodeSupport={setCodeSupport}
                isValidCode={isValidCode}
                setIsValidCode={setIsValidCode}
                isModalOpened={isModalOpened}
                setIsModalOpened={setIsModalOpened}
                isHistoryModalOpened={isHistoryModalOpened}
                setIsHistoryModalOpened={setIsHistoryModalOpened}
                isAssociateModalOpened={isAssociateModalOpened}
                selectedRows={selectedRows}
                setGlobalDataIsUpdated={handleSetGlobalDataIsUpdated}
                setLimit={setLimit}
                isConsultModalOpened={isConsultModalOpened}
            />
        </React.Fragment>
    );
}
